import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../context/userContext";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Chip, Tooltip } from "@mui/material";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { findDuplicateIdSet } from '../../utils/duplicateMatch';
import { DownloadIcon } from "@phosphor-icons/react";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { convertDateTimeToDateString, handleDisplayCreatedOnOrUpdatedOn } from "../../utils/datetimeUtils";
import { handlePriceWithRM } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";
import CommissionProgressBar from "./commission/commissionProgressBar";
import { facetFilterOptions } from './facetFilter';
import { archiveSale } from '../../api/salesApi';
import { useNavigate } from 'react-router-dom';
import useTableState from '../../hooks/useTableState';

export default function GenericSalesList({
    fetchDataApi,
    ModalComponent,
    modalExtraProps = {},
    onCreate,
    title,
    subtitle,
    showArchiveToggle = false,
}) {
    const { user } = useUser();
    const navigate = useNavigate();
    const ts = useTableState('', { defaultSorting: [{ id: 'depositDate', desc: true }] });
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSalesModal, setShowSalesModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    // Status tab drives both the archived fetch (Archived tab) and a client-side
    // status filter (Active / Pending / Refunded). 'All' = all non-archived.
    const [statusTab, setStatusTab] = useState('All');
    const showArchived = statusTab === 'Archived';
    const [sortKey, setSortKey] = useState('newest');
    const canViewArchived = user?.roles?.some(r => ['Super Admin', 'Accountant'].includes(r));
    const fetchDataApiRef = useRef(fetchDataApi);
    fetchDataApiRef.current = fetchDataApi;

    const fetchSalesData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchDataApiRef.current(showArchived);
            setSales(response.data);
        } catch (error) {
            console.error("Error fetching sales data:", error);
        } finally {
            setLoading(false);
        }
    }, [showArchived]);

    useEffect(() => {
        fetchSalesData();
    }, [fetchSalesData]);

    // Client-side status filter for the non-archived tabs.
    const visibleSales = useMemo(() => {
        if (statusTab === 'All' || statusTab === 'Archived') return sales;
        return sales.filter(s => (s.status || 'Pending') === statusTab);
    }, [sales, statusTab]);

    // Possible-duplicate flagging (client-side, WARN-only). List rows carry the
    // customer name + phone but no IC, so this matches on name (fuzzy) + phone.
    // Computed over all loaded rows so the flag is correct regardless of the
    // active status filter.
    const duplicateIds = useMemo(
        () => findDuplicateIdSet(sales, s => ({
            id: s.salesId,
            name: s.customerDetails?.customerName ?? '',
            phone: s.customerDetails?.phoneNumber ?? '',
            ic: '',
        })),
        [sales],
    );

    // Sort presets for the "Sort" dropdown above the table.
    const SORT_OPTIONS = {
        newest:    { label: 'Newest deposit', sorting: [{ id: 'depositDate', desc: true }] },
        oldest:    { label: 'Oldest deposit', sorting: [{ id: 'depositDate', desc: false }] },
        priceHigh: { label: 'Final Price: High → Low', sorting: [{ id: 'finalPrice', desc: true }] },
        priceLow:  { label: 'Final Price: Low → High', sorting: [{ id: 'finalPrice', desc: false }] },
        recent:    { label: 'Recently updated', sorting: [{ id: 'date', desc: true }] },
    };
    const applySort = (key) => { setSortKey(key); ts.setSorting(SORT_OPTIONS[key].sorting); };

    const STATUS_TABS = ['All', 'Active', 'Pending', 'Refunded', 'Dispute Pricing'];

    const handleViewSalesModal = (sale) => {
        setSelectedSale(sale);
        setShowSalesModal(true);
    };

    const handleCloseSalesModal = () => {
        setShowSalesModal(false);
        setSelectedSale(null);
    };

    const handleSaveSales = () => {
        fetchSalesData();
        setSelectedSale(null);
    };

    const handleArchive = async (sale) => {
        if (!window.confirm(`Archive Sale #${sale.salesId}? This is permanent and cannot be undone. The sale and its commission records will be hidden from all lists.`)) return;
        try {
            await archiveSale(sale.salesId);
            fetchSalesData();
        } catch (err) {
            alert('Failed: ' + (typeof err.response?.data === 'string' ? err.response.data : err.message));
        }
    };

    // Bulk archive for the selected (checkbox) rows. Caller pre-filters to the rows
    // the current user is allowed to archive (agents: own + Pending only).
    const handleArchiveSelected = async (rows, table) => {
        const n = rows.length;
        if (!window.confirm(`Archive ${n} sale${n === 1 ? '' : 's'}? This is permanent and cannot be undone. The sales and their commission records will be hidden from all lists.`)) return;
        let failed = 0;
        for (const r of rows) {
            try { await archiveSale(r.original.salesId); } catch { failed++; }
        }
        table.resetRowSelection();
        fetchSalesData();
        if (failed > 0) alert(`${failed} sale${failed === 1 ? '' : 's'} could not be archived.`);
    };

    const csvConfig = mkConfig({
        filename: 'Sales_Data_Export',
        fieldSeparator: ',',
        decimalSeparator: '.',
        useKeysAsHeaders: true,
    });

    const mapSalesForExport = (data) => data.map(s => ({
        'Sales ID': s.salesId,
        'Deposit Date': convertDateTimeToDateString(s.depositOn),
        'Customer': s.customerDetails?.customerName ?? '',
        'Phone': s.customerDetails?.phoneNumber ?? '',
        'Address': s.customerDetails?.address ?? '',
        'Agent': s.agentDetails ? handleAgentName(s.agentDetails.firstName, s.agentDetails.lastName, s.agentDetails.alias) : '',
        'Agent Group': s.agentDetails?.groupName ?? '',
        'Co-Agent (Offline)': s.coAgentDetails ? handleAgentName(s.coAgentDetails.firstName, s.coAgentDetails.lastName, s.coAgentDetails.alias) : '',
        'Co-broke Group': s.coAgentDetails?.groupName ?? '',
        'Category': s.productCategory,
        'Panel Qty': s.panelQty ?? '',
        'Energy (kWp)': s.energy ?? '',
        'Ori Price': s.originalPrice,
        'Astern Disc.': s.discountByAstern ?? 0,
        'Agent Disc.': s.discountByAgent ?? 0,
        // Upline / Team discount (spec §1) — funded by the seller's upline(s) / leader(s).
        'Upline Disc.': s.discountByUpline ?? 0,
        'Team Disc.': s.discountByTeam ?? 0,
        'Final Price': s.finalPrice,
        // Add-on Price = OTHER add-ons only (battery pulled out into its own columns, so
        // Final + Add-on + Battery Add-on = grand total with no double-count). (user 2026-07-15)
        'Add-on Price': Math.max(0, (s.addOnPrice ?? 0) - (s.batteryAddOnPrice ?? 0)),
        'Battery Qty': s.batteryQty ?? 0,
        'Battery (kWh)': s.batteryKwh ?? 0,
        'Battery Add-on': s.batteryAddOnPrice ?? 0,
        'Payment Method': s.paymentMethod ?? '',
        'Status': s.status ?? '',
        'Scheme': s.scheme ?? '',
        // On screen but never in the CSV, so an exported sheet could not answer
        // "how much has this customer actually paid?" — the column people export
        // for. Denominator matches the on-screen bar and the backend claim state
        // exactly: effectiveTotal ?? quotationTotal ?? (final + add-on), never a
        // flat final+add-on, or MIX/CC sales read wrong. (user 2026-09-08)
        'Cust Progress %': (() => {
            const total = Number(s.effectiveTotal) > 0 ? Number(s.effectiveTotal)
                : (Number(s.quotationTotal) > 0 ? Number(s.quotationTotal)
                : ((Number(s.finalPrice) || 0) + (Number(s.addOnPrice) || 0)));
            return total ? Math.round((Number(s.paidTotal) || 0) / total * 100) : 0;
        })(),
        'Customer Paid': Number(s.paidTotal) || 0,
        'Referral': s.referralName ?? '',
        'Date': handleDisplayCreatedOnOrUpdatedOn(s.createdOn, s.updatedOn) ?? '',
    }));

    const handleExportData = () => {
        const csv = generateCsv(csvConfig)(mapSalesForExport(visibleSales));
        download(csvConfig)(csv);
    };

    const handleExportSelected = (rows) => {
        const selectedData = rows.map(r => r.original);
        const csv = generateCsv(csvConfig)(mapSalesForExport(selectedData));
        download(csvConfig)(csv);
    };

    const columns = useMemo(() => [
        {
            id: 'dupFlag', header: '', size: 36, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => duplicateIds.has(row.original?.salesId)
                ? (
                    <Tooltip title="Possible duplicate (same name/phone/IC as other sale(s))">
                        <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: '1.05rem', verticalAlign: 'middle' }} />
                    </Tooltip>
                )
                : null,
        },
        { id: 'salesId', header: 'Sales ID', accessorKey: 'salesId', size: 80 },
        {
            id: 'status', header: 'Status', accessorKey: 'status', size: 90,
            Cell: ({ cell }) => {
                const v = cell.getValue() || 'Pending';
                const c = {
                    Active:   { bg: '#F0FDF4', fg: '#15803D' },
                    Pending:  { bg: '#FEF3C7', fg: '#B45309' },
                    Refunded: { bg: '#FEE2E2', fg: '#B91C1C' },
                    Archived: { bg: '#F1F5F9', fg: '#475569' },
                }[v] || { bg: '#F1F5F9', fg: '#475569' };
                return <Chip label={v} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, backgroundColor: c.bg, color: c.fg }} />;
            },
        },
        {
            id: 'depositDate', header: 'Deposit Date', size: 110,
            accessorFn: row => row.depositOn ? new Date(row.depositOn).getTime() : null,
            Cell: ({ row }) => convertDateTimeToDateString(row.original.depositOn),
        },
        {
            id: 'custProgress', header: 'Cust Progress', size: 110, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => (
                <CommissionProgressBar
                    paid={Number(row.original.paidTotal) || 0}
                    // Customer-progress denominator = the QUOTATION total the customer pays (financed
                    // for MIX/CC), NOT flat final+add-on. Same source-of-truth as the backend claim
                    // state + generalCommissionProgressList: effectiveTotal ?? quotationTotal ??
                    // (final + add-on). (user 2026-07-28)
                    total={
                        Number(row.original.effectiveTotal) > 0 ? Number(row.original.effectiveTotal)
                        : (Number(row.original.quotationTotal) > 0 ? Number(row.original.quotationTotal)
                        : ((Number(row.original.finalPrice) || 0) + (Number(row.original.addOnPrice) || 0)))
                    }
                />
            ),
        },
        { id: 'customerName', header: 'Customer', accessorFn: row => row.customerDetails?.customerName ?? '', size: 140 },
        { id: 'customerPhone', header: 'Phone', accessorFn: row => row.customerDetails?.phoneNumber ?? '', size: 120 },
        {
            id: 'agent', header: 'Agent', size: 140,
            accessorFn: row => {
                if (!row.agentDetails) return '';
                const name = handleAgentName(row.agentDetails.firstName, row.agentDetails.lastName, row.agentDetails.alias);
                return `${row.agentDetails?.groupName ?? ""} - ${name}`.trim();
            },
        },
        {
            id: 'coBroke', header: 'Co-broke', size: 140,
            accessorFn: row => {
                if (!row.coAgentDetails) return "";
                const name = handleAgentName(row.coAgentDetails.firstName, row.coAgentDetails.lastName, row.coAgentDetails.alias);
                return `${row.coAgentDetails?.groupName ?? ""} - ${name}`.trim();
            },
        },
        {
            id: 'referral', header: 'Referral', size: 140,
            accessorFn: row => row.referralName ?? '',
            Cell: ({ row }) => row.original?.referralName
                ? <span style={{ color: '#9D174D', fontWeight: 600 }}>{row.original.referralName}</span>
                : <span style={{ color: '#94A3B8' }}>—</span>,
        },
        { id: 'productCategory', header: 'Category', accessorKey: 'productCategory', size: 100 },
        { id: 'productEnergy', header: 'Energy', accessorFn: row => `${row.energy ?? ""} kWp`.trim(), size: 80 },
        {
            id: 'oriPrice', header: 'Ori Price', size: 100,
            accessorFn: row => row.originalPrice,
            Cell: ({ row }) => row.original?.originalPrice != null ? handlePriceWithRM(row.original.originalPrice) : "",
        },
        {
            id: 'asternDiscount', header: 'Astern Disc.', size: 100,
            accessorFn: row => row.discountByAstern,
            Cell: ({ row }) => {
                const v = row.original?.discountByAstern;
                if (v == null) return "";
                return <span style={{ color: v > 1000 ? '#EF4444' : 'inherit', fontWeight: v > 1000 ? 700 : 'inherit' }}>{handlePriceWithRM(v)}{v > 1000 ? ' ⚠️' : ''}</span>;
            },
        },
        {
            id: 'agentDiscount', header: 'Agent Disc.', size: 100,
            accessorFn: row => row.discountByAgent,
            Cell: ({ row }) => {
                const v = row.original?.discountByAgent;
                if (v == null) return "";
                return <span style={{ color: v > 1000 ? '#EF4444' : 'inherit', fontWeight: v > 1000 ? 700 : 'inherit' }}>{handlePriceWithRM(v)}{v > 1000 ? ' ⚠️' : ''}</span>;
            },
        },
        {
            // Upline Discount — funded by the seller's upline(s), comes off DOC. Same column
            // pattern + RM1,000 warn threshold as Astern/Agent above. (spec §1)
            id: 'uplineDiscount', header: 'Upline Disc.', size: 100,
            accessorFn: row => row.discountByUpline,
            Cell: ({ row }) => {
                const v = row.original?.discountByUpline;
                if (v == null) return "";
                return <span style={{ color: v > 1000 ? '#EF4444' : 'inherit', fontWeight: v > 1000 ? 700 : 'inherit' }}>{handlePriceWithRM(v)}{v > 1000 ? ' ⚠️' : ''}</span>;
            },
        },
        {
            // Team Discount — funded by the leader(s), comes off TOC.
            id: 'teamDiscount', header: 'Team Disc.', size: 100,
            accessorFn: row => row.discountByTeam,
            Cell: ({ row }) => {
                const v = row.original?.discountByTeam;
                if (v == null) return "";
                return <span style={{ color: v > 1000 ? '#EF4444' : 'inherit', fontWeight: v > 1000 ? 700 : 'inherit' }}>{handlePriceWithRM(v)}{v > 1000 ? ' ⚠️' : ''}</span>;
            },
        },
        {
            id: 'finalPrice', header: 'Final Price', size: 100,
            accessorFn: row => row.finalPrice,
            Cell: ({ row }) => row.original?.finalPrice != null ? handlePriceWithRM(row.original.finalPrice) : "",
        },
        {
            id: 'addOnPrice', header: 'Add-on Price', size: 100,
            accessorFn: row => row.addOnPrice,
            Cell: ({ row }) => row.original?.addOnPrice != null ? handlePriceWithRM(row.original.addOnPrice) : "",
        },
        {
            id: 'batteryQty', header: 'Battery Qty', size: 90,
            accessorFn: row => row.batteryQty ?? 0,
            Cell: ({ row }) => {
                const q = Number(row.original?.batteryQty) || 0;
                return q > 0 ? q : "";
            },
        },
        {
            id: 'batteryKwh', header: 'Battery (kWh)', size: 100,
            accessorFn: row => row.batteryKwh ?? 0,
            Cell: ({ row }) => {
                const k = Number(row.original?.batteryKwh) || 0;
                return k > 0 ? `${k} kWh` : "";
            },
        },
        { id: 'scheme', header: 'Scheme', accessorKey: 'scheme', size: 80,
          Cell: ({ cell }) => {
            const val = cell.getValue();
            return val ? <Chip label={val} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600,
              backgroundColor: val === 'NEM' ? '#FEF3C7' : '#DBEAFE',
              color: val === 'NEM' ? '#92400E' : '#1E40AF' }} /> : '';
          }
        },
        {
            id: 'date', header: 'Date', size: 100,
            accessorFn: (row) => {
                try { return new Date(handleDisplayCreatedOnOrUpdatedOn(row.createdOn, row.updatedOn)); }
                catch { return null; }
            },
            Cell: ({ row }) => {
                if (!row.original) return "";
                return convertDateTimeToDateString(handleDisplayCreatedOnOrUpdatedOn(row.original.createdOn, row.original.updatedOn));
            },
        },
        {
            id: 'actions', header: '', size: 120, enableSorting: false, enableColumnFilter: false,
            Cell: ({ row }) => {
                const canEdit = user?.roles?.some(r => ['Accountant', 'Super Admin', 'Admin', 'Sales Agent', 'In-house Agent', 'Astern Admin', 'Group Admin'].includes(r));
                // Super Admin / Accountant archive any sale; agents archive their OWN
                // sales only and ONLY while Pending — Active is locked (user 2026-06-07).
                const canArchiveAll = user?.roles?.some(r => ['Super Admin', 'Accountant'].includes(r));
                const isOwnSale = row.original?.agentDetails?.userId === user?.userId || row.original?.coAgentDetails?.userId === user?.userId;
                const canArchive = canArchiveAll || (isOwnSale && row.original?.status === 'Pending');
                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {canEdit && (
                            <span onClick={() => navigate(`/accounting/sales/${row.original.salesId}`)}
                                style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}>
                                Edit
                            </span>
                        )}
                        {user?.roleRights?.includes('AllSalesCommList') && (
                            <span onClick={() => navigate(`/accounting/sales/${row.original.salesId}/commission`)}
                                style={{ color: '#6366F1', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}>
                                View Comm
                            </span>
                        )}
                        {canArchive && !showArchived && (
                            <span onClick={() => handleArchive(row.original)}
                                style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}>
                                Archive
                            </span>
                        )}
                    </Box>
                );
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [duplicateIds]);

    const canEdit = user?.roles?.some(r => ['Accountant', 'Super Admin', 'Admin', 'Sales Agent', 'In-house Agent', 'Astern Admin', 'Group Admin'].includes(r));

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns,
        data: visibleSales,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        enableRowSelection: true,
        // Keep the restored page when navigating back from a sale's detail. The list
        // remounts with data=[] then fetches; react-table's default autoReset would
        // snap pageIndex back to 0 on that data change, wiping the sessionStorage-restored
        // page. Disable all three so the saved page/sort/filter survive. (user 2026-08-08)
        autoResetPageIndex: false,
        autoResetSorting: false,
        autoResetExpanded: false,
        initialState: { density: 'compact', showColumnFilters: false, showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (e) => {
                if (e.target.closest('input[type="checkbox"]') || e.target.closest('a') || (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer')) return;
                if (!canEdit) return;
                const url = `/accounting/sales/${row.original.salesId}`;
                if (e.ctrlKey || e.metaKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            },
            sx: {
                cursor: canEdit ? 'pointer' : 'default',
                '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' },
            },
        }),
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderTopToolbarCustomActions: ({ table }) => {
            const selectedRows = table.getSelectedRowModel().rows;
            const hasSelection = selectedRows.length > 0;
            // Rows the current user may archive: Super Admin / Accountant — any
            // non-archived selection; agents — their OWN sales and ONLY Pending
            // (Active is locked; user 2026-06-07).
            const canArchiveAll = user?.roles?.some(r => ['Super Admin', 'Accountant'].includes(r));
            const archivableRows = showArchived ? [] : selectedRows.filter(r => {
                const o = r.original;
                if (canArchiveAll) return true;
                const own = o?.agentDetails?.userId === user?.userId || o?.coAgentDetails?.userId === user?.userId;
                return own && o?.status === 'Pending';
            });
            return (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={handleExportData} style={{ border: '1px solid #E2E8F0', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DownloadIcon size={14} /> Export All
                    </button>
                    {hasSelection && (
                        <button onClick={() => handleExportSelected(selectedRows)} style={{ border: '1px solid #1A8CFF', background: '#EFF6FF', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#1A8CFF', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <DownloadIcon size={14} /> Export Selected ({selectedRows.length})
                        </button>
                    )}
                    {archivableRows.length > 0 && (
                        <button onClick={() => handleArchiveSelected(archivableRows, table)} style={{ border: '1px solid #EF4444', background: '#FEF2F2', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#EF4444', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Archive Selected ({archivableRows.length})
                        </button>
                    )}
                </Box>
            );
        },
    });

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            {/* Title */}
            {title && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{title}</h2>
                        {subtitle && <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>{subtitle}</p>}
                    </div>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                {onCreate && (
                    <button onClick={onCreate} style={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", cursor: 'pointer', marginRight: 8 }}>
                        Create
                    </button>
                )}

                {/* Status tabs: All / Active / Pending / Refunded (+ Archived for SA/Acct) */}
                <Box sx={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                    {STATUS_TABS.map((tab, i) => (
                        <button key={tab} onClick={() => setStatusTab(tab)}
                            style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", border: 'none', borderLeft: i === 0 ? 'none' : '1px solid #E2E8F0', cursor: 'pointer', background: statusTab === tab ? '#1A8CFF' : '#fff', color: statusTab === tab ? '#fff' : '#64748B' }}>
                            {tab}
                        </button>
                    ))}
                    {canViewArchived && (
                        <button onClick={() => setStatusTab('Archived')}
                            style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", border: 'none', borderLeft: '1px solid #E2E8F0', cursor: 'pointer', background: statusTab === 'Archived' ? '#EF4444' : '#fff', color: statusTab === 'Archived' ? '#fff' : '#64748B' }}>
                            Archived
                        </button>
                    )}
                </Box>

                {/* Sort dropdown */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>Sort:</span>
                    <select value={sortKey} onChange={(e) => applySort(e.target.value)}
                        style={{ padding: '6px 10px', fontSize: '0.78rem', fontFamily: "'Poppins', sans-serif", border: '1px solid #E2E8F0', borderRadius: '8px', background: '#fff', color: '#374151', cursor: 'pointer' }}>
                        {Object.entries(SORT_OPTIONS).map(([k, o]) => (
                            <option key={k} value={k}>{o.label}</option>
                        ))}
                    </select>
                </Box>
            </Box>
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
                {ModalComponent && (
                    <ModalComponent
                        show={showSalesModal}
                        onHide={handleCloseSalesModal}
                        selectedSale={selectedSale}
                        onSaved={handleSaveSales}
                        {...modalExtraProps}
                    />
                )}
            </div>
        </div>
    );
}
